import {MithrilComponent} from '@bitstillery/mithril'
import {Button, Dropdown, Icon} from '@bitstillery/common/components'
import {classes} from '@bitstillery/common/lib/utils'
import {activated_sort} from '@bitstillery/common/lib/filters'
import {state} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'

import type {CollectionProxy} from '@bitstillery/common/lib/collection'

/**
 * A sort option's label. The tuple a collection configures carries the English source of
 * `filters.sort.types.<by>`, so a key that has no placeholder stored yet still reads as words
 * rather than as its own name.
 */
function sort_option_label([by, label]: readonly string[]): string {
    return $t(`filters.sort.types.${by ?? ''}`, {defaultValue: label})
}

export interface CollectionSorterAttrs {
    className?: string
    /** Only its sort and modes are read, so a bare sort state (without a CollectionProxy) sorts as well. */
    collection: {
        state: Pick<CollectionProxy['state'], 'bulk_mode' | 'sort'> & {inline_edit_mode?: boolean}
    }
    disabled?: boolean
}

export class CollectionSorter extends MithrilComponent<CollectionSorterAttrs> {
    data = state(
        {
            toggle: false,
        },
        'collection-sorter-data',
    )

    view(vnode: m.Vnode<CollectionSorterAttrs>) {
        const {collection} = vnode.attrs

        // Gated on there being something to sort by rather than on the collection being ready: the sort
        // options are configuration, so a page that knows them can render this row server-side, where the
        // rows it sorts have not been fetched yet.
        const sort_options = collection.state.sort.options
        if (!sort_options.length) {
            return null
        }
        return (
            <div
                className={classes('c-collection-sorter', {
                    disabled: vnode.attrs.collection.state.bulk_mode || vnode.attrs.collection.state.inline_edit_mode,
                })}
            >
                {/* The field picker and the direction toggle form a single segmented control, so the
                    direction that's toggled visibly belongs to the field it reorders. The leading sort
                    glyph replaces the old "Sort by" label — the tooltip still names the control. */}
                <div className='sort-control'>
                    <Dropdown className={classes('sort-by-dropdown')} model={this.data}>
                        <Button
                            activated={this.data.toggle}
                            className={classes('sort-placeholder', {
                                modified: activated_sort(collection.state.sort).by,
                            })}
                            size='d'
                            tip={$t('filters.sort.options')}
                            type='unset'
                            variant='unset'
                        >
                            <Icon className='sort-glyph' name='sort' type='unset' />
                            <span>
                                {(() => {
                                    const sort_option = sort_options.find((o) => o[0] === collection.state.sort.by)
                                    if (sort_option) {
                                        return sort_option_label(sort_option)
                                    }
                                    return $t('filters.sort.options')
                                })()}
                            </span>
                        </Button>

                        <div className='dropdown-body'>
                            {sort_options.map((option) => (
                                <button
                                    disabled={vnode.attrs.disabled}
                                    className={classes('sort-item', {active: option[0] === collection.state.sort.by})}
                                    onclick={() => {
                                        collection.state.sort.by = option[0] ?? ''
                                    }}
                                >
                                    {sort_option_label(option)}
                                </button>
                            ))}
                        </div>
                    </Dropdown>
                    <Button
                        className={classes('sort-direction', {
                            modified: activated_sort(collection.state.sort).order,
                        })}
                        disabled={collection.state.bulk_mode}
                        icon={collection.state.sort.order === 'desc' ? 'sortDescending' : 'sortAscending'}
                        onclick={(e) => {
                            e.stopPropagation()
                            if (collection.state.sort.order === 'asc') {
                                collection.state.sort.order = 'desc'
                            } else {
                                collection.state.sort.order = 'asc'
                            }
                            this.data.toggle = false
                        }}
                        size='d'
                        tip={$t('filters.sort.direction_tip')}
                        type='unset'
                        variant='unset'
                    />
                </div>
            </div>
        )
    }
}
